import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '../auth/AuthContext'
import { useLanguage } from '../shared/LanguageContext'
import { fetchTaHome, submitTaAction } from './taApi'
import TaHomeView from './TaHomeView'
import TaMeView from './TaMeView'
import TaScheduleView from './TaScheduleView'
import { TaHeader, TaNavigation } from './TaShell'
import { taCopy } from './taCopy'
import TaWorkView from './TaWorkView'
import './ta.css'

const VALID_VIEWS = new Set(['home', 'schedule', 'work', 'me'])

function initialView() {
  const value = window.location.hash.replace('#', '')
  return VALID_VIEWS.has(value) ? value : 'home'
}

export default function TaLayout() {
  const { portalToken } = useAuth()
  const { language, setDefaultLanguage } = useLanguage()
  const copy = taCopy(language)

  useEffect(() => {
    setDefaultLanguage('en')
  }, [setDefaultLanguage])
  const [home, setHome] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [active, setActive] = useState(initialView)
  const [focusTarget, setFocusTarget] = useState(null)
  const requestRef = useRef(0)

  const load = useCallback(async (showLoading = false) => {
    if (!portalToken) return
    const requestId = requestRef.current + 1
    requestRef.current = requestId
    if (showLoading) setLoading(true)
    const result = await fetchTaHome(portalToken)
    if (requestId !== requestRef.current) return
    if (result.error) {
      setError(result.error)
    } else {
      setHome(result.data)
      setError(null)
    }
    setLoading(false)
  }, [portalToken])

  useEffect(() => { load(true) }, [load])

  useEffect(() => {
    document.body.classList.add('ta-v2-body')
    return () => document.body.classList.remove('ta-v2-body')
  }, [])

  useEffect(() => {
    const onHash = () => setActive(initialView())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  function navigate(view, target = null) {
    setActive(view)
    setFocusTarget(target)
    window.history.pushState(null, '', `#${view}`)
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  async function action(payload) {
    const result = await submitTaAction(portalToken, payload)
    if (!result.error) await load(false)
    return result
  }

  return (
    <div className="ta-v2 portal-shell">
      <a className="ta-v2-skip" href="#ta-main">{copy('skip')}</a>
      <TaHeader name={home?.ta?.name || ''} />
      <main id="ta-main" className="ta-v2-main" tabIndex="-1">
        {loading && !home ? (
          <div className="ta-v2-loading" role="status"><span />{copy('loading')}</div>
        ) : error && !home ? (
          <div className="ta-v2-load-error" role="alert">
            <p>{copy('loadFailed')}</p>
            <button type="button" className="ta-v2-secondary-button" onClick={() => load(true)}>{copy('refresh')}</button>
          </div>
        ) : home ? (
          <>
            {error && <p className="ta-v2-unavailable" role="status">{copy('dataUnavailable')}</p>}
            {active === 'home' && <TaHomeView home={home} onNavigate={navigate} />}
            {active === 'schedule' && <TaScheduleView home={home} />}
            {active === 'work' && <TaWorkView home={home} onAction={action} focusTarget={focusTarget} />}
            {active === 'me' && <TaMeView home={home} onAction={action} focusTarget={focusTarget} />}
          </>
        ) : null}
      </main>
      <TaNavigation active={active} onChange={navigate} />
    </div>
  )
}
